import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  IconButton,
  TextField,
  FormControl,
  Autocomplete as MUIAutocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import "./serviceManForm.css";
import { useGoogleMaps } from "../../../../../../../../../../../utils/mapConfig";
import { Autocomplete as GoogleAutocomplete } from "@react-google-maps/api";

const ServiceManForm = ({
  open,
  onClose,
  serviceMan,
  serviceList,
  onSubmit,
}) => {
  const { isLoaded } = useGoogleMaps();
  const autocompleteRef = useRef(null);

  const [formData, setFormData] = useState({
    serviceManName: "",
    phone: "",
    address: "",
    location: "",
    service: [],
    experience: "",
    district: "",
    email: "",
    postalName: "",
    zipNumber: "",
    profilePic: null,
    profilePicUrl: null,
  });

  const [errors, setErrors] = useState({});

  const serviceNamesWithIds = serviceList.map((service) => ({
    id: service.id,
    name: service.service,
  }));

  useEffect(() => {
    if (serviceMan) {
      setFormData({
        serviceManName: serviceMan.worker_name,
        phone: serviceMan.mobile_number,
        address: serviceMan.address,
        location: serviceMan.location,
        service: serviceMan.service,
        experience: serviceMan.experience,
        district: serviceMan.district,
        email: serviceMan.email,
        postalName: serviceMan.postal_name,
        zipNumber: serviceMan.zipnumber,
        profilePic: null,
        profilePicUrl: serviceMan.worker_image || null,
      });
    } else {
      setFormData({
        serviceManName: "",
        phone: "",
        address: "",
        location: "",
        service: [],
        experience: "",
        district: "",
        email: "",
        postalName: "",
        zipNumber: "",
        profilePic: null,
        profilePicUrl: null,
      });
    }
    setErrors({});
  }, [serviceMan]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newValue =
      name === "experience" && value ? parseInt(value, 10) : value;
    setFormData((prevData) => ({
      ...prevData,
      [name]: newValue,
    }));

    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setFormData((prevData) => ({
        ...prevData,
        profilePic: file,
        profilePicUrl: url,
      }));
      setErrors((prevErrors) => ({ ...prevErrors, profilePic: "" }));
    }
  };

  useEffect(() => {
    return () => {
      if (formData.profilePicUrl) {
        URL.revokeObjectURL(formData.profilePicUrl);
      }
    };
  }, [formData.profilePicUrl]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.serviceManName) newErrors.serviceManName = "Name is required";
    if (!formData.phone || !/^\d{10}$/.test(formData.phone))
      newErrors.phone = "A valid 10-digit phone number is required";
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "A valid email is required";
    if (!formData.service) newErrors.service = "Please select a service";
    if (!formData.address) newErrors.address = "Address is required";
    if (!formData.location) newErrors.location = "Location is required";
    if (!formData.district) newErrors.district = "District is required";
    if (!formData.postalName) newErrors.postalName = "District is required";
    if (
      formData.experience &&
      (!Number.isInteger(Number(formData.experience)) ||
        formData.experience < 0)
    )
      newErrors.experience = "Experience must be a non-negative whole number";
    if (formData.zipNumber && !/^\d{6}$/.test(formData.zipNumber))
      newErrors.zipNumber = "A valid 6-digit zip code is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const serviceDetails = { ...formData };
      onSubmit(serviceDetails);
      onClose();
    }
  };

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place && place.formatted_address) {
        setFormData((prevState) => ({
          ...prevState,
          location: place.formatted_address,
        }));
      }
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby="service-dialog-title"
      >
        <DialogTitle id="service-dialog-title" style={{ color: "#ff4d4d" }}>
          {serviceMan ? "Update Service Man" : "Add New Service Man"}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8, color: "grey.500" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            Please enter the service man details below:
          </Typography>
          <div className="profile-pic-container">
            <div className="profile">
              <label
                style={{
                  background: formData.profilePicUrl
                    ? `url(${formData.profilePicUrl}) center / cover`
                    : "rgb(182, 170, 170)",
                }}
              >
                {!formData.profilePicUrl && (
                  <span className="user">
                    <AccountCircleIcon style={{ fontSize: "15rem" }} />
                  </span>
                )}
                <span className="camera">
                  <AddIcon />
                </span>
                <input type="file" onChange={handleFileChange} />
              </label>
            </div>
          </div>
          <TextField
            autoFocus
            margin="dense"
            name="serviceManName"
            label="Service Man Name"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.serviceManName}
            onChange={handleChange}
            error={!!errors.serviceManName}
            helperText={errors.serviceManName}
          />
          <FormControl fullWidth margin="dense" error={!!errors.service}>
            <MUIAutocomplete
              multiple
              options={serviceNamesWithIds}
              getOptionLabel={(option) => option.name}
              value={serviceNamesWithIds.filter((service) =>
                formData.service.includes(service.id)
              )}
              onChange={(event, newValue) =>
                handleChange({
                  target: {
                    name: "service",
                    value: newValue.map((service) => service.id),
                  },
                })
              }
              renderInput={(params) => (
                <TextField {...params} label="Select Service" />
              )}
            />
          </FormControl>
          <TextField
            margin="dense"
            name="phone"
            label="Phone Number"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.phone}
            onChange={handleChange}
            error={!!errors.phone}
            helperText={errors.phone}
          />
          <TextField
            margin="dense"
            name="experience"
            label="Experience (Years)"
            type="number"
            fullWidth
            variant="outlined"
            value={formData.experience}
            onChange={handleChange}
            error={!!errors.experience}
            helperText={errors.experience}
          />
          <TextField
            margin="dense"
            name="address"
            label="Address"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={formData.address}
            onChange={handleChange}
            error={!!errors.address}
            helperText={errors.address}
          />
          {isLoaded && (
            <GoogleAutocomplete
              onLoad={(autocomplete) =>
                (autocompleteRef.current = autocomplete)
              }
              onPlaceChanged={handlePlaceChanged}
            >
              <TextField
                margin="dense"
                name="location"
                label="Location"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.location}
                onChange={handleChange}
                error={!!errors.location}
                helperText={errors.location}
              />
            </GoogleAutocomplete>
          )}
          <TextField
            margin="dense"
            name="district"
            label="District"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.district}
            onChange={handleChange}
            error={!!errors.district}
            helperText={errors.district}
          />
          <TextField
            margin="dense"
            name="email"
            label="Email"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
          />
          <TextField
            margin="dense"
            name="postalName"
            label="Postal Name"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.postalName}
            onChange={handleChange}
            error={!!errors.postalName}
            helperText={errors.postalName}
          />
          <TextField
            margin="dense"
            name="zipNumber"
            label="Zip Number"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.zipNumber}
            onChange={handleChange}
            error={!!errors.zipNumber}
            helperText={errors.zipNumber}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            variant="contained"
            color="error"
            style={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="success"
            style={{ textTransform: "none" }}
          >
            {serviceMan ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ServiceManForm;
